<template lang="pug">
div
  h2 {{ $t("properties") }}
  el-alert(
    v-if="error",
    type="warning",
    :title="$t('genericError')",
    :closable="false"
  )
  template(v-else)
    .profile-fields-header(v-if="hasEditableTagTypes")
      el-button(
        v-if="!isEditing",
        text,
        type="primary",
        :icon="'Edit'",
        @click="startEditing"
      ) {{ $t("edit") }}
    AdminForm(
      v-if="isEditing",
      ref="formRef",
      v-loading="isLoading",
      :model="editForm",
      @submit="saveChanges"
    )
      AdminFormRow
        AdminFormField(
          v-for="row in editableRows",
          :key="row.tagTypeUuid",
          :label="row.tagTypeName",
          :prop="row.tagTypeUuid",
          :control-id="tagValueControlId(row.tagTypeUuid)"
        )
          template(#default="field")
            el-select(
              :id="tagValueControlId(row.tagTypeUuid)",
              v-model="editForm[row.tagTypeUuid]",
              filterable,
              clearable,
              style="width: 100%",
              :aria-describedby="field.ariaDescribedby",
              :aria-invalid="field.ariaInvalid",
              :aria-required="field.ariaRequired",
              :disabled="field.disabled"
            )
              el-option(
                v-for="opt in availableValuesByType[row.tagTypeUuid] || []",
                :key="opt.uuid",
                :label="opt.valueText",
                :value="opt.uuid"
              )
      template(#actions)
        .profile-fields-header
          el-button(
            type="primary",
            :loading="saveLoading",
            @click="submitForm"
          ) {{ $t("save") }}
          el-button(@click="cancelEdit") {{ $t("cancel") }}
    el-table(
      v-if="isEditing && nonEditableRows.length > 0",
      v-loading="isLoading",
      :data="nonEditableRows",
      style="width: 100%"
    )
      el-table-column(:label="$t('property')", prop="valueText")
      el-table-column(:label="$t('type')", prop="tagTypeName")
    el-table(
      v-if="!isEditing",
      v-loading="isLoading",
      :data="tableData",
      style="width: 100%"
    )
      el-table-column(:label="$t('property')", prop="valueText")
        template(#default="scope")
          span {{ scope.row.valueText }}
      el-table-column(:label="$t('type')", prop="tagTypeName")
</template>

<script setup lang="ts">
import AdminForm from "@/components/elements/admin-form/AdminForm.vue";
import AdminFormField from "@/components/elements/admin-form/AdminFormField.vue";
import AdminFormRow from "@/components/elements/admin-form/AdminFormRow.vue";
import tagService from "@/services/tags";
import { getSetting } from "@/getSettings";
import type { LoadState } from "@/store/loadingStates";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

defineOptions({ name: "UserProfileProperties" });

interface UserData {
  id: number;
}

interface TagType {
  uuid: string;
  name: string;
}

interface TagValue {
  uuid: string;
  tagType: string;
  valueText: string;
}

interface UserTag extends TagValue {
  value: string;
}

interface ProfilePropertyRow {
  valueText: string;
  tagTypeUuid: string;
  tagTypeName: string;
  assignmentUuid: string | null;
  currentValueUuid: string | null;
}

const store = useStore();
const { t } = useI18n();
const formRef = ref<InstanceType<typeof AdminForm>>();
const isEditing = ref(false);
const saveLoading = ref(false);
const editForm = ref<Record<string, string | null>>({});

const userTagsLoadingState = computed<LoadState<UserTag[]>>(
  () => store.getters.userTagsLoadingState as LoadState<UserTag[]>,
);
const tagTypesState = computed<LoadState<TagType[]>>(
  () => store.getters.tagTypesState as LoadState<TagType[]>,
);
const tagsLoadingState = computed<LoadState<TagValue[]>>(
  () => store.getters.tagsLoadingState as LoadState<TagValue[]>,
);
const userId = computed(() => (store.getters.getUserData as UserData).id);
const userTags = computed(() => userTagsLoadingState.value.result ?? []);
const tagTypes = computed(() => tagTypesState.value.result ?? []);
const tagValues = computed(() => tagsLoadingState.value.result ?? []);
const error = computed(
  () =>
    userTagsLoadingState.value.error ||
    tagTypesState.value.error ||
    tagsLoadingState.value.error,
);
const isLoading = computed(
  () =>
    userTagsLoadingState.value.loading ||
    tagTypesState.value.loading ||
    tagsLoadingState.value.loading,
);
const editableTagTypeNames = computed(() => getSetting("userEditableTagTypes"));
const hasEditableTagTypes = computed(
  () => editableTagTypeNames.value.length > 0,
);
const editableTagTypeUuids = computed(() =>
  editableTagTypeNames.value
    .map((name) => tagTypes.value.find((type) => type.name === name))
    .filter((type): type is TagType => type !== undefined)
    .map((type) => type.uuid),
);
const availableValuesByType = computed<Record<string, TagValue[]>>(() =>
  Object.fromEntries(
    editableTagTypeUuids.value.map((typeUuid) => [
      typeUuid,
      tagValues.value.filter((value) => value.tagType === typeUuid),
    ]),
  ),
);

function getTagTypeName(tagTypeUuid: string): string {
  return tagTypes.value.find((item) => item.uuid === tagTypeUuid)?.name ?? "";
}

const editableRows = computed<ProfilePropertyRow[]>(() =>
  editableTagTypeUuids.value.map((typeUuid) => {
    const current = userTags.value.find((tag) => tag.tagType === typeUuid);
    return {
      valueText: current?.valueText ?? "",
      tagTypeUuid: typeUuid,
      tagTypeName: getTagTypeName(typeUuid),
      assignmentUuid: current?.uuid ?? null,
      currentValueUuid: current?.value ?? null,
    };
  }),
);
const nonEditableRows = computed<ProfilePropertyRow[]>(() => {
  const editableSet = new Set(editableTagTypeUuids.value);
  return userTags.value
    .filter((tag) => !editableSet.has(tag.tagType))
    .map<ProfilePropertyRow>((tag) => ({
      valueText: tag.valueText,
      tagTypeUuid: tag.tagType,
      tagTypeName: getTagTypeName(tag.tagType),
      assignmentUuid: tag.uuid,
      currentValueUuid: tag.value,
    }));
});
const tableData = computed<ProfilePropertyRow[]>(() => {
  const assignedEditableRows = editableRows.value.filter(
    (row) => row.assignmentUuid !== null,
  );

  return [...assignedEditableRows, ...nonEditableRows.value];
});

function tagValueControlId(tagTypeUuid: string): string {
  return `user-profile-tag-${tagTypeUuid}`;
}

function startEditing(): void {
  void store.dispatch("loadTags");
  editForm.value = Object.fromEntries(
    editableRows.value.map((row) => [
      row.tagTypeUuid,
      row.currentValueUuid ?? "",
    ]),
  );
  isEditing.value = true;
}

function cancelEdit(): void {
  isEditing.value = false;
  editForm.value = {};
}

function submitForm(): void {
  void formRef.value?.submit();
}

async function saveChanges(): Promise<void> {
  saveLoading.value = true;
  try {
    const operations: Promise<unknown>[] = [];
    for (const row of editableRows.value) {
      const current = userTags.value.find(
        (tag) => tag.tagType === row.tagTypeUuid,
      );
      const newValueUuid = editForm.value[row.tagTypeUuid] ?? "";
      const oldValueUuid = current?.value ?? "";
      if (oldValueUuid === newValueUuid) continue;

      if (current) {
        operations.push(
          tagService.removeTagFromUser(
            userId.value,
            row.tagTypeUuid,
            current.uuid,
          ),
        );
      }
      if (newValueUuid) {
        operations.push(
          tagService.addTagForUser(
            userId.value.toString(),
            row.tagTypeUuid,
            newValueUuid,
          ),
        );
      }
    }

    await Promise.all(operations);
    void store.dispatch("loadTagsForUser", { userId: userId.value });
    isEditing.value = false;
    editForm.value = {};
  } catch {
    store.commit("addNewNotification", {
      title: "error",
      text: "genericError",
      notificationType: "error",
    });
  } finally {
    saveLoading.value = false;
  }
}

onMounted(() => {
  void store.dispatch("loadTagsForUser", { userId: userId.value });
  void store.dispatch("loadTagTypes");
});
</script>

<style scoped>
.profile-fields-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}
</style>
